<template>
  <div class="secondaries-form">
    <!-- Buy/Sell Tab Navigation -->
    <div class="trade-tabs">
      <button
        type="button"
        class="trade-tab"
        :class="{ active: tradeType === 'Buy' }"
        @click="switchTradeType('Buy')"
      >
        <svg viewBox="0 0 20 20" fill="none">
          <path d="M10 16V4M10 4L6 8M10 4L14 8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        Buy {{ unitTitlePlural }}
      </button>
      <button
        type="button"
        class="trade-tab"
        :class="{ active: tradeType === 'Sell', disabled: singleTokenBalance <= 0 }"
        :aria-describedby="singleTokenBalance <= 0 ? 'sell-tab-tooltip' : null"
        @click="switchTradeType('Sell')"
        @mouseover="singleTokenBalance <= 0 ? (showTooltip = true) : ''"
        @mouseleave="showTooltip = false"
        @focus="singleTokenBalance <= 0 ? (showTooltip = true) : ''"
        @blur="showTooltip = false"
      >
        <svg viewBox="0 0 20 20" fill="none">
          <path d="M10 4V16M10 16L6 12M10 16L14 12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        Sell {{ unitTitlePlural }}
      </button>
      <!-- Outside the tab so it isn't faded by the disabled state, and right-aligned so it stays in the panel. -->
      <span
        v-if="singleTokenBalance <= 0 && showTooltip"
        id="sell-tab-tooltip"
        role="tooltip"
        class="sell-tooltip"
      >
        You don't have {{ unitPlural }} to sell.
      </span>
    </div>

    <!-- Balance: the company's cash to buy with, or what it holds to sell -->
    <div class="balance-card">
      <div class="balance-icon" :class="{ sell: tradeType === 'Sell' }">
        <svg v-if="tradeType === 'Buy'" viewBox="0 0 24 24" fill="none">
          <path d="M21 18V19C21 20.1 20.1 21 19 21H5C3.89 21 3 20.1 3 19V5C3 3.9 3.89 3 5 3H19C20.1 3 21 3.9 21 5V6H12C10.89 6 10 6.9 10 8V16C10 17.1 10.89 18 12 18H21ZM12 16H22V8H12V16ZM16 13.5C15.17 13.5 14.5 12.83 14.5 12C14.5 11.17 15.17 10.5 16 10.5C16.83 10.5 17.5 11.17 17.5 12C17.5 12.83 16.83 13.5 16 13.5Z" fill="currentColor"/>
        </svg>
        <svg v-else viewBox="0 0 24 24" fill="none">
          <path d="M4 6H2V20C2 21.1 2.9 22 4 22H18V20H4V6ZM20 2H8C6.9 2 6 2.9 6 4V16C6 17.1 6.9 18 8 18H20C21.1 18 22 17.1 22 16V4C22 2.9 21.1 2 20 2ZM20 16H8V4H20V16ZM10 9H18V11H10V9ZM10 12H14V14H10V12ZM10 6H18V8H10V6Z" fill="currentColor"/>
        </svg>
      </div>
      <div class="balance-info">
        <span class="balance-label">{{ tradeType === 'Buy' ? 'Available Cash' : `Available ${tokenInfo.symbol} ${unitTitlePlural}` }}</span>
        <span class="balance-value">{{ tradeType === 'Buy' ? formatAmountToDollar(balance, 2, currency) : Number(singleTokenBalance).toLocaleString() }}</span>
      </div>
    </div>

    <!-- Trade Form -->
    <form class="trade-form" @submit.prevent="showPopUp">
      <div class="form-section">
        <div class="section-header">
          <h3 class="section-title">How many {{ unitPlural }} do you want to {{ tradeType.toLowerCase() }}?</h3>
          <p class="section-subtitle">
            Last {{ tradeType === 'Sell' ? 'bought' : 'sold' }} at
            <span class="price-highlight">{{ formatAmountToDollar(marketPrice, 2, currency) }}</span> per {{ unitSingular }}
          </p>
        </div>

        <!-- Keyed on the side: the input keeps its own text, so without a remount the number
             typed on Buy would still show on Sell after the payload under it was cleared. -->
        <TradeNumberInput
          :key="tradeType"
          placeHolder="0.00"
          label="shares amount"
          :amount="payload.tokens"
          :maxNumber="tradeType === 'Sell' ? maxSellToken : maxBuyToken"
          :checkValidity="tokenValid"
          :tradeType="tradeType"
          type="text"
          fieldType="tokenAmount"
          name="tokenAmount"
          id="tokenAmount"
          @set="setTokenAmount"
          @valid="tokenAmountValid"
          @validate="validate"
        />
      </div>

      <!-- Set your own price. A collapsed row, as on mobile: left closed, or opened and left
           blank, the order fills at market, so the default path is untouched and opening the
           row is never a dead end. -->
      <div v-if="limitPricingAvailable" class="advanced-price">
        <button
          type="button"
          class="advanced-row"
          :aria-expanded="advancedOpen"
          aria-controls="limit-price-panel"
          @click="toggleAdvanced"
        >
          <svg class="advanced-chevron" :class="{ open: advancedOpen }" viewBox="0 0 20 20" fill="none" aria-hidden="true">
            <path d="M6 8L10 12L14 8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          <span class="advanced-row-label">Set your own price</span>
          <span class="advanced-pill">Advanced</span>
        </button>

        <div v-if="advancedOpen" id="limit-price-panel" class="form-section advanced-panel">
          <h3 class="section-title">Price per {{ unitSingular }} to {{ tradeType.toLowerCase() }} at ({{ currency.toUpperCase() }})</h3>

          <TradeNumberInput
            placeHolder="0.00"
            label="limit price"
            :amount="payload.limitPrice"
            :minNumber="0"
            :maxNumber="Infinity"
            :tradeType="tradeType"
            type="text"
            fieldType="limitPrice"
            name="limitPrice"
            id="limitPrice"
            @set="setLimitPrice"
          />
          <p class="advanced-hint">Leave blank to trade at the market price.</p>

          <!-- What the price being set is judged against. Always the market price; fair value
               beside it where the two differ, so the gap between them is the thing you read
               rather than something to work out. On equity there is only the one column. -->
          <div class="fair-value-card">
            <div class="fair-value-row">
              <div class="fair-value-col">
                <span class="fair-value-label">Market price</span>
                <span class="fair-value-market">{{ formatAmountToDollar(marketPrice, 2, currency) }}</span>
              </div>
              <div v-if="hasDistinctFairValue" class="fair-value-col">
                <span class="fair-value-label">
                  Fair value
                  <Tooltip
                    :text="
                      tradeType === 'Sell'
                        ? 'What one token is worth today: its market price plus the interest it has earned since the last coupon, after withholding tax. Price below this and you hand that interest to the buyer.'
                        : 'What one token is worth today: its market price plus the interest it has earned since the last coupon, after withholding tax. Price above this and you are paying for interest before it is due.'
                    "
                  />
                </span>
                <span class="fair-value-fair">{{ formatAmountToDollar(fairValue, 2, currency) }}</span>
              </div>
            </div>
            <p v-if="hasDistinctFairValue" class="fair-value-note">
              <template v-if="accruedPerToken >= 0">
                Includes {{ formatAmountToDollar(accruedPerToken, 2, currency) }} of interest earned
                since the last coupon, after withholding tax.
              </template>
              <template v-else>
                Market price is {{ formatAmountToDollar(-accruedPerToken, 2, currency) }} above fair
                value.
              </template>
            </p>
          </div>

          <!-- Out of band blocks the order; merely far from the reference only warns. Both name
               the reference the band is measured against, so the reader corrects against it. -->
          <p v-if="priceOutsideBand" class="advanced-error" role="alert">
            Enter a price between {{ formatAmountToDollar(limitPriceFloor, 2, currency) }} and
            {{ formatAmountToDollar(limitPriceCap, 2, currency) }}, within 50% of the {{ referenceLabel }}.
          </p>
          <p v-else-if="priceOffMarket" class="advanced-warning">
            That is {{ Math.round(priceDrift * 100) }}% {{ limitPrice > referencePrice ? "above" : "below" }} the
            {{ referenceLabel }}, so this order may take a while to fill.
          </p>
        </div>
      </div>

      <!-- Order Summary. A seller sees what reaches the wallet, the same figure the
           confirmation shows, rather than finding it out after placing the order. -->
      <div class="summary-card" :class="{ error: tradeType === 'Buy' && totalAmountError }">
        <div class="summary-header">
          <span class="summary-title">Order Summary</span>
        </div>
        <div class="summary-content">
          <div class="summary-row">
            <span class="summary-label">{{ unitTitlePlural }}</span>
            <span class="summary-value">{{ payload.tokens ? Number(payload.tokens).toLocaleString() : '0' }}</span>
          </div>
          <div class="summary-row">
            <span class="summary-label">{{ isLimit ? 'Your price' : 'Price' }} per {{ unitSingular }}</span>
            <span class="summary-value">{{ formatAmountToDollar(orderPrice, 2, currency) }}</span>
          </div>
          <div class="summary-row">
            <span class="summary-label">Transaction fee ({{ tradeFee }}%)</span>
            <span class="summary-value">{{ formatAmountToDollar(feeAmount, 2, currency) }}</span>
          </div>
          <div class="summary-divider"></div>
          <div class="summary-row total">
            <span class="summary-label">{{ tradeType === 'Buy' ? 'Total you pay' : 'You receive' }}</span>
            <span class="summary-value">{{ formatAmountToDollar(tradeType === 'Buy' ? buyTotal : sellNet, 2, currency) }}</span>
          </div>
        </div>
        <p v-if="tradeType === 'Buy' && totalAmountError" class="error-text">
          Your wallet has {{ formatAmountToDollar(balance, 2, currency) }}
        </p>
      </div>

      <button type="submit" class="submit-btn" :class="{ sell: tradeType === 'Sell' }" :disabled="priceOutsideBand">
        <svg v-if="tradeType === 'Buy'" viewBox="0 0 20 20" fill="none">
          <path d="M10 16V4M10 4L6 8M10 4L14 8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        <svg v-else viewBox="0 0 20 20" fill="none">
          <path d="M10 4V16M10 16L6 12M10 16L14 12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        Place {{ tradeType }} Order
      </button>
    </form>

    <!-- Confirmation, then the company PIN -->
    <Teleport to="#teleport-modal">
      <SmallModal
        v-if="showConfirmModal"
        class="tw-z-50"
        @close="showConfirmModal = !showConfirmModal"
      >
        <template v-slot:content>
          <div class="confirm-modal">
            <h3 class="confirm-title">Confirm {{ tradeType }} Order</h3>

            <div class="confirm-card">
              <div class="confirm-header">
                <div class="confirm-asset">
                  <img
                    v-if="tokenInfo.image"
                    class="asset-image"
                    :src="tokenInfo.image"
                    :alt="tokenInfo.name"
                  />
                  <div class="asset-info">
                    <span class="asset-name">{{ tokenInfo.name }}</span>
                    <span class="asset-shares">{{ Number(payload.tokens || 0).toLocaleString() }} {{ Number(payload.tokens) === 1 ? unitSingular : unitPlural }}</span>
                  </div>
                </div>
              </div>

              <div class="confirm-details">
                <div class="detail-row">
                  <span class="detail-label">
                    {{ tradeType === 'Buy' ? 'Amount' : 'You receive' }}{{ isLimit ? `, at ${formatAmountToDollar(orderPrice, 2, currency)} per ${unitSingular}` : ', at the last price' }}
                  </span>
                  <span class="detail-value">{{ formatAmountToDollar(tradeType === 'Buy' ? Number(totalAmount) || 0 : sellNet, 2, currency) }}</span>
                </div>
                <div class="detail-row">
                  <span class="detail-label">Transaction Fee ({{ tradeFee }}%)</span>
                  <span class="detail-value">{{ formatAmountToDollar(feeAmount, 2, currency) }}</span>
                </div>
              </div>
            </div>

            <PinConfirmation
              class="pin-section"
              :btnTitle="'Confirm ' + tradeType"
              @confirmed="buyToken"
            />
          </div>
        </template>
      </SmallModal>
    </Teleport>

    <!-- Market Trade Info Modal -->
    <Teleport to="#teleport-modal">
      <SmallModal
        v-if="showInfoModal && !isLimit"
        class="tw-z-50"
        @close="showInfoModal = !showInfoModal"
      >
        <template v-slot:content>
          <SecondaryTradeInfoModal
            header="You are about to invest with Market Price"
            subHeader=""
            :details="marketTradeInfo"
            type="marketTrade"
            @close="closeInfoModal"
          />
        </template>
      </SmallModal>
    </Teleport>

    <!-- Advanced Trade Info Modal -->
    <Teleport to="#teleport-modal">
      <SmallModal
        v-if="showInfoModal && isLimit"
        class="tw-z-50"
        @close="showInfoModal = !showInfoModal"
      >
        <template v-slot:content>
          <SecondaryTradeInfoModal
            header="You are about to trade at your own price"
            subHeader=""
            :details="marketAdvanceTradeInfo"
            type="marketTrade"
            @close="closeInfoModal"
          />
        </template>
      </SmallModal>
    </Teleport>
  </div>
</template>

<script>
  import { mapState, mapActions, mapGetters } from "vuex";
  import { formatAmountToDollar } from "@/utils/helpers";
  import { HELD_TO_MATURITY_NOTE, isHeldToMaturity } from "@/utils/held-to-maturity";
  import { unitWord } from "@/utils/trade-view";
  import Tooltip from "@/components/general/Tooltip.vue";
  import TradeNumberInput from "@/components/wallet/TradeNumberInput.vue";
  import SmallModal from "@/components/general/SmallModal.vue";
  import SecondaryTradeInfoModal from "@/components/trade/SecondaryTradeInfoModal.vue";
  import PinConfirmation from "@/components/general/PinConfirmation.vue";

  // A company pays half the deal's fee each way, at the real price (GE-Backend exchange-process
  // getBuyTransactionFee / getSellTransactionFee, for an organisation that is not an API key).
  const COMPANY_FEE_SHARE = 0.5;

  // The same bounds the mobile app puts on a price you set: 50% either side of the reference
  // is the hard limit, beyond 20% only a warning.
  const LIMIT_PRICE_BAND = 0.5;
  const LIMIT_PRICE_WARN_BAND = 0.2;

  export default {
    name: "SecondariesTokenAmountForm",

    emits: ["completed", "switch", "showInfo"],

    components: {
      Tooltip,
      TradeNumberInput,
      SmallModal,
      SecondaryTradeInfoModal,
      PinConfirmation,
    },

    data() {
      return {
        showInfoModal: false,
        // The "Set your own price" row. Collapsed by default.
        advancedOpen: false,
        showConfirmModal: false,
        tokenValid: false,
        totalAmountError: false,
        showTooltip: false,
        validInput: {
          tokenAmount: false,
        },
        payload: {
          tokens: "",
          limitPrice: "",
        },
        tradeType: "Buy",
        marketTradeInfo: [
          {
            text: "Buying or selling shares is not instant.",
          },
          {
            text: "You can’t be sure of the price your order will be executed, due to market fluctuations.",
          },
          {
            text: "Your buy/sell request will be added to a list and you will be credited when someone accepts your order.",
          },
        ],
        marketAdvanceTradeInfo: [
          {
            text: "Buying or selling shares is not instant.",
          },
          {
            text: "Your order will only be filled at the price you set or better.",
          },
          {
            text: "Your buy/sell request will be added to a list and you will be credited when someone accepts your order.",
          },
        ],
      };
    },

    computed: {
      ...mapState({
        singleTokenBalance: (state) => state.TokenModule.tokenBalance,
        heldToMaturity: (state) => isHeldToMaturity(state.TokenModule.tokenPayoutDetails),
        tokenInfo: (state) => state.TokenModule.tokenInfo,
      }),
      ...mapGetters("organisationModule", ["walletBalance"]),
      currency() {
        return this.tokenInfo.currency || "USD";
      },
      // The organisation's wallet in the deal's own currency.
      balance() {
        const wallet = (this.walletBalance || []).find((w) => w.currency === this.currency);
        return Number(wallet?.balance) || 0;
      },
      // "share" on equity and notes, "token" on a commodity (trade-view).
      unitSingular() { return unitWord(this.tokenInfo, 1); },
      unitPlural() { return unitWord(this.tokenInfo, 2); },
      unitTitlePlural() { return this.unitPlural.charAt(0).toUpperCase() + this.unitPlural.slice(1); },
      // The side's fee, halved for a company as the backend charges it.
      tradeFee() {
        const fee = this.tradeType === "Sell" ? this.tokenInfo.sell_fee : this.tokenInfo.buy_fee;
        return (Number(fee) || 0) * COMPANY_FEE_SHARE;
      },
      totalAmount() { return (Number(this.payload.tokens) || 0) * this.orderPrice; },
      feeAmount() { return ((Number(this.totalAmount) || 0) * this.tradeFee) / 100; },
      buyTotal() { return (Number(this.totalAmount) || 0) + this.feeAmount; },
      sellNet() { return Math.max((Number(this.totalAmount) || 0) - this.feeAmount, 0); },
      // A market order fills at the last traded price; one at the company's own price at that.
      orderPrice() { return this.isLimit ? this.limitPrice : this.marketPrice; },
      maxSellToken() {
        return Number(this.singleTokenBalance) || 0;
      },
      // What a named price is judged against. The market price is what a MARKET order fills at;
      // fair value is what one unit is WORTH, which on a coupon-bearing deal is the price plus
      // the interest accrued since its last coupon, net of withholding. The API computes it and
      // sends it on the token payload as `fairPrice`.
      marketPrice() {
        return Number(this.tokenInfo?.price?.exchange) || 0;
      },
      fairValue() {
        return Number(this.tokenInfo?.fairPrice) || 0;
      },
      // Worth stating separately only when the two round to different numbers on screen. On
      // equity there is no coupon to accrue and they never differ; on a bond they coincide on
      // the day a coupon pays. Saying it twice in either case adds nothing.
      hasDistinctFairValue() {
        return (
          this.fairValue > 0 &&
          Math.abs(this.fairValue - this.marketPrice) >= 0.01
        );
      },
      accruedPerToken() {
        return this.fairValue - this.marketPrice;
      },
      maxBuyToken() {
        const max = this.orderPrice > 0 ? Number((this.balance / this.orderPrice).toFixed(2)) : NaN;
        return Number.isFinite(max) ? max : 10000000000000;
      },

      // Setting your own price, as on mobile. Offered on every deal type once the raise has
      // completed: while a deal is still raising the price is the deal's, not the market's, and
      // naming your own would mean nothing.
      limitPricingAvailable() {
        return this.tokenInfo?.completed_raise === true;
      },
      limitPrice() {
        return Number(this.payload.limitPrice) || 0;
      },
      // A limit order only once a price has actually been named. An open-but-blank row stays a
      // market order.
      isLimit() {
        return this.limitPricingAvailable && this.advancedOpen && this.limitPrice > 0;
      },
      // What a named price is measured against: fair value where the API sends one, else the
      // market price, so the band never silently widens to nothing.
      referencePrice() {
        return this.fairValue > 0 ? this.fairValue : this.marketPrice;
      },
      referenceLabel() {
        return this.hasDistinctFairValue ? "fair value" : "market price";
      },
      // Hard bounds: 50% either side of the reference. Beyond 20% the price is allowed but
      // unlikely to fill quickly, so it is flagged rather than blocked. A token quoted at 0 has
      // no reference and takes any positive price.
      limitPriceFloor() {
        return this.referencePrice * (1 - LIMIT_PRICE_BAND);
      },
      limitPriceCap() {
        return this.referencePrice * (1 + LIMIT_PRICE_BAND);
      },
      priceOutsideBand() {
        return (
          this.isLimit &&
          this.referencePrice > 0 &&
          (this.limitPrice < this.limitPriceFloor || this.limitPrice > this.limitPriceCap)
        );
      },
      priceDrift() {
        return this.referencePrice > 0
          ? Math.abs(this.limitPrice - this.referencePrice) / this.referencePrice
          : 0;
      },
      priceOffMarket() {
        return this.isLimit && !this.priceOutsideBand && this.priceDrift > LIMIT_PRICE_WARN_BAND;
      },
    },

    methods: {
      ...mapActions("TokenModule", ["tradeToken"]),
      ...mapActions(["showToast"]),

      formatAmountToDollar,

      async buyToken() {
        // Monthly payouts are held to maturity; the backend refuses these trades too.
        if (this.heldToMaturity) {
          this.showToast({ description: HELD_TO_MATURITY_NOTE, display: true, type: "error" });
          this.showConfirmModal = false;
          return;
        }
        const payload = {
          id: this.tokenInfo._id,
          amount: Number(this.payload.tokens),
          type: this.tradeType.toLowerCase(),
          // No price is the backend's "fill at market".
          ...(this.isLimit ? { price: this.limitPrice } : {}),
        };
        try {
          // A refusal (no deal-room access on a private deal, too little cash) comes back with
          // the backend's reason, which the store shows as a toast.
          const response = await this.tradeToken(payload);
          if (response) {
            this.showToast({
              description: "Your order has been placed",
              display: true,
              type: "success",
            });
            this.$emit("completed");
          }
        } finally {
          this.showConfirmModal = false;
        }
      },

      closeInfoModal() {
        this.showInfoModal = !this.showInfoModal;
        this.showConfirmModal = !this.showConfirmModal;
      },

      showPopUp() {
        // The real minimum, as the investor app checks it before a buy order.
        const minInvestment = Number(this.tokenInfo.min_trade?.buy) || 0;
        if (this.tradeType === "Buy" && (Number(this.totalAmount) || 0) < minInvestment) {
          this.tokenValid = true;
          this.showToast({
            description: `Minimum investment is ${this.formatAmountToDollar(minInvestment, 2, this.currency)}`,
            display: true,
            type: "error",
          });
          return;
        }
        // Out of band is already blocked at the button; this covers Enter in a field.
        if (this.priceOutsideBand) return;
        if (this.tradeType === "Buy" && this.totalAmount > this.balance) {
          this.totalAmountError = true;
          return;
        }
        this.tokenValid = true;
        setTimeout(() => {
          if (!this.validInput.tokenAmount) return;
          this.showInfoModal = !this.showInfoModal;
        }, 500);
      },

      resetAmounts() {
        this.totalAmountError = false;
        this.payload.tokens = "";
        this.payload.limitPrice = "";
        this.advancedOpen = false;
      },

      switchTradeType(tradeType) {
        if (this.singleTokenBalance <= 0) return;
        this.tradeType = tradeType;
        this.resetAmounts();
      },

      setTokenAmount(amount) {
        this.totalAmountError = false;
        this.payload.tokens = amount;
      },

      setLimitPrice(price) {
        this.totalAmountError = false;
        this.payload.limitPrice = price;
      },

      // Closing the row drops the price with it: the field unmounts, and a price kept behind a
      // closed row would be one the reader can no longer see.
      toggleAdvanced() {
        this.advancedOpen = !this.advancedOpen;
        if (!this.advancedOpen) this.payload.limitPrice = "";
        this.totalAmountError = false;
      },

      tokenAmountValid(valid) {
        this.validInput.tokenAmount = valid;
      },

      validate() {
        this.tokenValid = false;
      },
    },
  };
</script>

<style lang="scss" scoped>
// As investor-app's trade drawer.
$bg-primary: #F8FAFC;
$bg-card: #FFFFFF;
$text-primary: #0F172A;
$text-secondary: #475569;
$text-muted: #94A3B8;
$border-color: #E2E8F0;
$accent: #4D84FF;
$btn-primary: #001240;
$btn-primary-hover: #06206B;
$warning: #F59E0B;
$error: #EF4444;

.secondaries-form {
  padding-bottom: 24px;
}

// Trade Tabs
.trade-tabs {
  position: relative;
  display: flex;
  border-bottom: 1px solid $border-color;
  padding: 0 20px;
}

.trade-tab {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  flex: 1;
  padding: 14px 16px;
  background: transparent;
  border: none;
  border-bottom: 2px solid transparent;
  font-size: 14px;
  font-weight: 400;
  color: $text-muted;
  cursor: pointer;
  transition: color 0.2s ease, border-color 0.2s ease;
  position: relative;

  svg {
    width: 16px;
    height: 16px;
  }

  &:hover:not(.disabled) {
    color: $text-primary;
  }

  &.active {
    color: $btn-primary;
    border-bottom-color: $btn-primary;
  }

  &.disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.sell-tooltip {
  position: absolute;
  top: calc(100% + 6px);
  right: 20px;
  z-index: 20;
  max-width: calc(100% - 40px);
  padding: 8px 12px;
  background: $text-primary;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.12);
  font-size: 12px;
  line-height: 1.4;
  color: #FFFFFF;
  pointer-events: none;
}

// Balance Card
.balance-card {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  background: linear-gradient(135deg, rgba($btn-primary, 0.03) 0%, rgba($accent, 0.05) 100%);
  border-bottom: 1px solid $border-color;
}

.balance-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: rgba($btn-primary, 0.08);
  border-radius: 8px;
  flex-shrink: 0;

  svg {
    width: 20px;
    height: 20px;
    color: $btn-primary;
  }

  &.sell {
    background: rgba($error, 0.08);

    svg {
      color: $error;
    }
  }
}

.balance-info {
  display: flex;
  flex-direction: column;
}

.balance-label {
  font-size: 12px;
  font-weight: 400;
  color: $text-muted;
}

.balance-value {
  font-size: 18px;
  font-weight: 400;
  color: $text-primary;
}

// Trade Form
.trade-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 20px;
}

.form-section {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.section-header {
  margin-bottom: 4px;
}

.section-title {
  font-size: 15px;
  font-weight: 400;
  color: $text-primary;
  margin: 0 0 4px;
}

.section-subtitle {
  font-size: 13px;
  font-weight: 400;
  color: $text-secondary;
  margin: 0;
}

.price-highlight {
  color: $btn-primary;
  font-weight: 500;
}

// Set your own price: a collapsed row under the quantity, as on mobile.
.advanced-price {
  display: flex;
  flex-direction: column;
  margin-top: -8px;
}

.advanced-row {
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  padding: 10px 0;
  background: transparent;
  border: none;
  cursor: pointer;
  text-align: left;
}

.advanced-chevron {
  width: 20px;
  height: 20px;
  color: $accent;
  flex-shrink: 0;
  transition: transform 150ms ease;

  &.open {
    transform: rotate(180deg);
  }
}

.advanced-row-label {
  flex: 1;
  font-size: 13px;
  font-weight: 400;
  color: $accent;
}

.advanced-pill {
  padding: 3px 8px;
  background: $bg-primary;
  border: 1px solid $border-color;
  border-radius: 5px;
  font-size: 11px;
  font-weight: 400;
  color: $btn-primary;
}

.advanced-panel {
  padding-top: 4px;
}

.advanced-hint {
  margin: -4px 0 0;
  font-size: 12px;
  font-weight: 400;
  color: $text-muted;
}

.advanced-error,
.advanced-warning {
  margin: 0;
  font-size: 12px;
  font-weight: 400;
  line-height: 1.4;
}

.advanced-error {
  color: $error;
}

.advanced-warning {
  color: darken($warning, 12%);
}

// What a named price is judged against. Sits directly under the price inside the "Set your
// own price" row, so the reference and the input it qualifies stay together.
.fair-value-card {
  padding: 12px;
  background: $bg-primary;
  border-radius: 6px;
}

.fair-value-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.fair-value-col {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
}

.fair-value-label {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  font-weight: 400;
  color: $text-muted;
}

.fair-value-market {
  font-size: 14px;
  font-weight: 400;
  color: $text-muted;
}

// The one the eye should land on: it is the number the price is being judged against.
.fair-value-fair {
  font-size: 14px;
  font-weight: 500;
  color: $text-primary;
}

.fair-value-note {
  margin: 10px 0 0;
  padding-top: 10px;
  border-top: 1px solid $border-color;
  font-size: 11px;
  font-weight: 400;
  color: $text-muted;
}

// Summary Card
.summary-card {
  background: $bg-card;
  border: 1px solid $border-color;
  border-radius: 10px;
  overflow: hidden;

  &.error {
    border-color: $error;
  }
}

.summary-header {
  padding: 12px 16px;
  background: $bg-primary;
  border-bottom: 1px solid $border-color;
}

.summary-title {
  font-size: 13px;
  font-weight: 400;
  color: $text-secondary;
}

.summary-content {
  padding: 16px;
}

.summary-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 6px 0;

  &.total {
    padding-top: 12px;

    .summary-label,
    .summary-value {
      font-size: 15px;
      color: $text-primary;
    }
  }
}

.summary-label {
  font-size: 13px;
  font-weight: 400;
  color: $text-secondary;
}

.summary-value {
  font-size: 14px;
  font-weight: 400;
  color: $text-primary;
  text-align: right;
}

.summary-divider {
  height: 1px;
  background: $border-color;
  margin: 8px 0;
}

.error-text {
  font-size: 12px;
  color: $error;
  text-align: center;
  padding: 0 16px 12px;
  margin: 0;
}

// Submit Button
.submit-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 14px 24px;
  background: $btn-primary;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 400;
  cursor: pointer;
  transition: background 0.2s ease;

  svg {
    width: 18px;
    height: 18px;
  }

  &:hover {
    background: $btn-primary-hover;
  }

  &.sell {
    background: $error;

    &:hover {
      background: darken($error, 8%);
    }
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

// Confirmation Modal
.confirm-modal {
  padding: 20px;
}

.confirm-title {
  font-size: 18px;
  font-weight: 400;
  color: $text-primary;
  text-align: center;
  margin: 0 0 20px;
}

.confirm-card {
  background: $bg-primary;
  border-radius: 10px;
  overflow: hidden;
}

.confirm-header {
  padding: 16px;
  border-bottom: 1px solid $border-color;
}

.confirm-asset {
  display: flex;
  align-items: center;
  gap: 12px;
}

.asset-image {
  width: 44px;
  height: 44px;
  border-radius: 8px;
  object-fit: cover;
}

.asset-info {
  display: flex;
  flex-direction: column;
}

.asset-name {
  font-size: 15px;
  font-weight: 400;
  color: $text-primary;
}

.asset-shares {
  font-size: 13px;
  font-weight: 400;
  color: $text-secondary;
}

.confirm-details {
  padding: 16px;
}

.detail-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 6px 0;
}

.detail-label {
  font-size: 13px;
  font-weight: 400;
  color: $text-secondary;
}

.detail-value {
  font-size: 14px;
  font-weight: 400;
  color: $text-primary;
}

.pin-section {
  margin-top: 16px;
}
</style>
